/**
 * Motivos de contestação apresentados ao gerador.
 *
 * Os rótulos vêm da RFC (seção 3) e cada um carrega o par `object_type` + `contestation_type` que
 * a API espera. No item da coleta os motivos são agrupados por natureza: "sobre a coleta" abre uma
 * contestação de `COLLECT`, "sobre o lançamento financeiro" abre de `TRANSACTION` — o gerador não
 * precisa conhecer o conceito de transação para chegar nela.
 */

import type {
  ContestationObjectType,
  ContestationType,
} from '@/integrations/contestacao/client';

export interface ContestationMotive {
  value: string;
  label: string;
  description: string;
  object_type: ContestationObjectType;
  contestation_type: ContestationType;
}

export interface ContestationMotiveGroup {
  label: string;
  motives: ContestationMotive[];
}

export const OBJECT_TYPE_LABELS: Record<ContestationObjectType, string> = {
  COLLECT: 'Coleta',
  TRANSACTION: 'Lançamento financeiro',
  BILLING_CYCLE: 'Fatura',
};

/**
 * Motivos de uma coleta, nos rótulos definidos pela PD em 24/08/2026 (retorno de QA).
 *
 * Peso e unidade aparecem **separados** para o gerador, mas abrem a mesma contestação
 * (`COLLECT:DATA`): a API trata os dois como dados da coleta e aceita a correção de um sem o
 * outro. A separação é de leitura — quem está olhando a linha sabe qual dos dois números está
 * errado, e escolher "Unidade divergente" quando o problema é o peso obrigaria a explicar isso
 * na justificativa.
 *
 * Consequência: `value` precisa ser único por opção, então ele deixa de ser
 * `object_type:contestation_type`. O caminho de volta (histórico) usa
 * `MOTIVE_HISTORY_LABELS` — ver a nota lá.
 */
const COLLECT_MOTIVES: ContestationMotive[] = [
  {
    value: 'COLLECT:DATA:WEIGHT',
    label: 'Peso divergente',
    description: 'O peso registrado nesta coleta está errado',
    object_type: 'COLLECT',
    contestation_type: 'DATA',
  },
  {
    value: 'COLLECT:DATA:UNITS',
    label: 'Unidade divergente',
    description: 'A quantidade de unidades registrada nesta coleta está errada',
    object_type: 'COLLECT',
    contestation_type: 'DATA',
  },
  {
    value: 'COLLECT:EXISTENCE',
    label: 'Coleta não realizada',
    description: 'Esta coleta não aconteceu',
    object_type: 'COLLECT',
    contestation_type: 'EXISTENCE',
  },
];

const TRANSACTION_MOTIVES: ContestationMotive[] = [
  {
    value: 'TRANSACTION:INCORRECT_VALUE',
    label: 'Valor calculado incorreto',
    description: 'O valor calculado nesta transação está errado',
    object_type: 'TRANSACTION',
    contestation_type: 'INCORRECT_VALUE',
  },
  {
    value: 'TRANSACTION:UNDUE_TRANSACTION',
    label: 'Lançamento indevido',
    description: 'Esta transação não deveria existir',
    object_type: 'TRANSACTION',
    contestation_type: 'UNDUE_TRANSACTION',
  },
];

const BILLING_CYCLE_MOTIVES: ContestationMotive[] = [
  {
    value: 'BILLING_CYCLE:INCORRECT_TOTAL',
    label: 'Valor total incorreto',
    description: 'O valor total desta fatura está errado',
    object_type: 'BILLING_CYCLE',
    contestation_type: 'INCORRECT_TOTAL',
  },
  {
    value: 'BILLING_CYCLE:UNDUE_BILLING',
    label: 'Medição indevida',
    description: 'Esta fatura não deveria ter sido gerada',
    object_type: 'BILLING_CYCLE',
    contestation_type: 'UNDUE_BILLING',
  },
];

/** Motivos da medição inteira. */
export const MEASUREMENT_MOTIVE_GROUPS: ContestationMotiveGroup[] = [
  { label: 'Contestação de medição completa', motives: BILLING_CYCLE_MOTIVES },
];

/**
 * Motivos de um item da listagem de coletas. O grupo do lançamento financeiro só entra quando a
 * coleta tem transação vinculada — fatura de valor fixo não tem, e oferecer o motivo levaria a uma
 * abertura sem `object_id`.
 */
export function collectMotiveGroups(
  hasTransaction: boolean,
): ContestationMotiveGroup[] {
  const groups: ContestationMotiveGroup[] = [
    { label: 'Contestação por coleta', motives: COLLECT_MOTIVES },
  ];

  if (hasTransaction) {
    groups.push({
      label: 'Sobre o lançamento financeiro',
      motives: TRANSACTION_MOTIVES,
    });
  }

  return groups;
}

export function findMotive(
  groups: ContestationMotiveGroup[],
  value: string,
): ContestationMotive | undefined {
  for (const group of groups) {
    const motive = group.motives.find((m) => m.value === value);
    if (motive) return motive;
  }
  return undefined;
}

/**
 * Rótulo do caminho de volta: `(object_type, contestation_type)` → texto do histórico.
 *
 * Existe como mapa próprio porque a ida **não** é injetiva: "Peso divergente" e "Unidade
 * divergente" abrem o mesmo `COLLECT:DATA`, e procurar na lista de opções devolveria sempre a
 * primeira — o histórico diria "Peso divergente" numa contestação aberta como unidade. Um
 * rótulo que cobre os dois é honesto; um que escolhe o errado, não.
 */
const MOTIVE_HISTORY_LABELS: Record<string, string> = {
  'COLLECT:DATA': 'Peso ou unidade divergente',
  'COLLECT:EXISTENCE': 'Coleta não realizada',
  // Saiu da lista de motivos ofertáveis em 08/09/2026 (coleta não tem valor; o valor é da
  // `transaction`), mas continua aqui: contestação já gravada com esse par ainda aparece no
  // histórico, e sem o rótulo ela exibiria `VALUE` cru.
  'COLLECT:VALUE': 'Valor divergente',
  'TRANSACTION:INCORRECT_VALUE': 'Valor calculado incorreto',
  'TRANSACTION:UNDUE_TRANSACTION': 'Lançamento indevido',
  'BILLING_CYCLE:INCORRECT_TOTAL': 'Valor total incorreto',
  'BILLING_CYCLE:UNDUE_BILLING': 'Medição indevida',
};

/** Rótulo de um motivo já registrado, para exibir no histórico. */
export function motiveLabel(
  objectType: ContestationObjectType,
  contestationType: ContestationType,
): string {
  return MOTIVE_HISTORY_LABELS[`${objectType}:${contestationType}`] ?? contestationType;
}
